import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { Inbox, Link2, X } from 'lucide-react';
import { api } from '../api/client';
import { formatDateTime } from '../lib/format';

// Side drawer showing a full RawIpnLog: metadata, outcome, rejection reason,
// the raw payload JSON, and a link to the BillingEvent it produced (if any).
// Triggered from RawIpnLogsPage row click; closes via the X or backdrop.
export default function RawIpnLogDrawer({ id, open, onClose }) {
  const query = useQuery({
    queryKey: ['admin-raw-ipn-log', id],
    queryFn: () => api.get(`/api/admin/raw-ipn-logs/${id}`).then((r) => r.data),
    enabled: Boolean(id) && open,
  });

  if (!open) return null;

  const log = query.data?.log;
  const linked = log?.billingEventId;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <aside className="ml-auto relative w-full max-w-xl h-full overflow-y-auto bg-white shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="section-icon">
              <Inbox className="w-4 h-4" />
            </span>
            <span className="font-medium">Raw IPN log</span>
          </div>
          <button type="button" className="btn-ghost text-xs" onClick={onClose}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          {query.isLoading && <div className="text-ink-400">Loading…</div>}
          {query.error && (
            <div className="text-rose-600">Failed to load: {query.error.message}</div>
          )}

          {log && (
            <>
              <DetailRow label="Received" value={formatDateTime(log.receivedAt)} />
              <DetailRow label="Outcome" value={log.outcome} />
              {log.rejectionReason && (
                <DetailRow
                  label="Reason"
                  value={<span className="text-rose-600">{log.rejectionReason}</span>}
                />
              )}
              <DetailRow label="Provider" value={log.provider} />
              <DetailRow label="HTTP status" value={String(log.httpStatus)} />
              <DetailRow label="Source IP" value={log.sourceIp || '—'} mono />
              <DetailRow label="User agent" value={log.userAgent || '—'} />
              {log.resellerSlug && <DetailRow label="Reseller slug" value={log.resellerSlug} mono />}
              <DetailRow label="Customer" value={log.customerEmail || '—'} />
              <DetailRow label="Transaction ID" value={log.externalTxnId || '—'} mono />
              <DetailRow label="Event type" value={log.eventType || '—'} />

              {linked && (
                <>
                  <SectionHeader>Linked billing event</SectionHeader>
                  <DetailRow label="Txn" value={linked.externalTxnId} mono />
                  <DetailRow label="Event" value={linked.eventType} />
                  <DetailRow label="Customer" value={linked.customerEmail} />
                  <div className="ml-32 mt-1">
                    <RouterLink
                      to={`/admin/sales?q=${encodeURIComponent(linked.externalTxnId || '')}`}
                      className="inline-flex items-center gap-1.5 text-xs text-brand-700 hover:underline"
                    >
                      <Link2 className="w-3 h-3" /> View in Sales
                    </RouterLink>
                  </div>
                </>
              )}

              <SectionHeader>Raw payload</SectionHeader>
              {/* open by default — inspecting the payload is the primary reason to open this drawer */}
              <details className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-xs" open>
                <summary className="cursor-pointer text-ink-500 select-none">
                  Show JSON ({Object.keys(log.rawBody || {}).length} keys)
                </summary>
                <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all text-ink-700">
                  {JSON.stringify(log.rawBody, null, 2)}
                </pre>
              </details>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-xs uppercase tracking-wide text-ink-400 w-32 shrink-0">{label}</span>
      <span className={`text-ink-800 ${mono ? 'font-mono text-xs' : ''} break-all`}>{value}</span>
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <h4 className="text-xs uppercase tracking-wide text-ink-500 pt-3 mt-3 border-t border-gray-100">
      {children}
    </h4>
  );
}
